import { ReactElement, useMemo } from 'react'
import { I18N } from 'typings/custom'
import { Body } from 'syf-component-library/ui/typography'
import { CustomerSupportContacts } from 'typings/brands'
import { Stack } from 'syf-component-library/ui/spacing'
import styled from 'styled-components'
import NoMarginAlertBanner from './NoMarginAlertBanner'

/**
 * Banner used to display Transmit 0001 / 0007 / 0017 / 0418 error messages
 */
export const TransmitSystemOrApplicationErrorBanner = ({
  i18n
}: {
  i18n: I18N
}): ReactElement => {
  return (
    <NoMarginAlertBanner type="error" scrollToOnMount>
      {i18n({
        transmitErrorMessages:
          'We apologize. We are currently experiencing technical difficulties and are unable to access your account. Please try again later.'
      })}
    </NoMarginAlertBanner>
  )
}

/**
 * Wrapper for a single row which will hold one of the items in the CSR contact
 * list in the alert banner.
 */
const CSRContact = styled(Body)`
  display: flex;
  flex-wrap: wrap;
  white-space: pre-wrap;
`

/** Prevent the phone number from wrapping in the middle. */
const Phone = styled(Body).attrs({ isInline: true })`
  white-space: nowrap;
`

/**
 * Banner used to display Transmit 0003
 */
export const TransmitInvalidInputErrorBanner = ({
  i18n,
  assistanceAlertContacts
}: {
  i18n: I18N
  assistanceAlertContacts: CustomerSupportContacts['assistanceAlertContacts']
}): ReactElement => {
  const multiLineContacts = useMemo(
    () =>
      assistanceAlertContacts.map((contact, idx) => {
        const isNotLast = idx < assistanceAlertContacts.length - 1

        const hasLabel = contact.includes(':')
        if (!hasLabel) {
          return (
            <>
              <CSRContact key={contact}>
                <div>{contact}</div>
              </CSRContact>
              {isNotLast && <Stack />}
            </>
          )
        }

        const [label, phone] = contact.split(':')
        return (
          <>
            <CSRContact key={phone}>
              <div>{label}: </div>
              <div>
                <Phone>{phone}</Phone>
              </div>
            </CSRContact>
            {isNotLast && <Stack />}
          </>
        )
      }),
    [assistanceAlertContacts]
  )

  return (
    <NoMarginAlertBanner type="error" scrollToOnMount>
      <Body>
        {i18n({
          transmitErrorMessages:
            'We apologize. We’re experiencing technical difficulties. For assistance, please call us at'
        })}
      </Body>
      <Stack size="base" />
      {multiLineContacts}
    </NoMarginAlertBanner>
  )
}

/**
 * Banner used to display Transmit 0004
 */
export const TransmitUserCancelledErrorBanner = ({
  i18n
}: {
  i18n: I18N
}): ReactElement => {
  return (
    <NoMarginAlertBanner type="error" scrollToOnMount>
      {i18n({
        transmitErrorMessages:
          'The verification process was cancelled. If the cancellation was an error, please try again.'
      })}
    </NoMarginAlertBanner>
  )
}

/**
 * Banner used to display Transmit 0005
 */
export const TransmitAuthenticationFailureErrorBanner = ({
  i18n
}: {
  i18n: I18N
}): ReactElement => {
  return (
    <NoMarginAlertBanner type="error" scrollToOnMount>
      {i18n({
        transmitErrorMessages:
          'You’ve reached the maximum number of attempts. To receive a new code, please try again.'
      })}
    </NoMarginAlertBanner>
  )
}

/**
 * Banner used to display Transmit 0006
 */
export const TransmitAuthSessionExpiredErrorBanner = ({
  i18n
}: {
  i18n: I18N
}): ReactElement => {
  return (
    <NoMarginAlertBanner type="error" scrollToOnMount>
      {i18n({
        transmitErrorMessages: 'Your session has timed out. Please try again.'
      })}
    </NoMarginAlertBanner>
  )
}
